/**
 * Photographs handed to this app by the phone's own share sheet.
 *
 * The installed app registers itself as a share target in the manifest (see
 * `share_target` in vite.config.ts), so picking pictures in the gallery and
 * tapping Share lists Puppy Doctor beside the messengers. What the system then
 * does is POST them to `/share` as a multipart form — a real navigation, with
 * files in the body — which no page can read, because the page the browser is
 * about to load does not exist yet.
 *
 * THE SERVICE WORKER IS THE ONLY THING THAT CAN CATCH IT. It answers that POST
 * itself (see src/sw.ts), puts the files here, and redirects to the same path as
 * a plain GET carrying an id. The app boots, reads the id out of its own URL and
 * claims the files. This module is the whole of the hand-off, and it is imported
 * by both sides — the worker and the page are separate bundles, and a key
 * spelled twice is a key that gets spelled differently once.
 *
 * IndexedDB rather than a `postMessage`, because there is no page to message:
 * the share usually *launches* the app, and the worker has finished answering
 * the request before the first frame is painted. `idb-keyval` is already here
 * for the query cache, and a `File` survives a structured clone.
 *
 * ONE RECORD, UNDER ONE KEY. A share that is never opened — the app is killed on
 * the way in, the phone is locked and forgotten — would otherwise leave its
 * megabytes in IndexedDB with nothing that would ever look at them again. One
 * key means the next share overwrites the last, so the store holds at most one
 * afternoon's pictures rather than a year of abandoned ones. It also means the
 * id is checked rather than trusted: a stale `/share?shared=…` left in the history
 * claims nothing.
 *
 * THIS IS ANDROID AND THE DESKTOP. WebKit implements no share target, so on iOS
 * the app does not appear in the share sheet and nothing here ever runs; the
 * pictures go in through the wizard's own picker, which is where they went
 * before. Nothing needs to change here when that lands.
 */
import { del, get, set } from 'idb-keyval';

/**
 * Where the system posts, and where the app lands afterwards. Written out again
 * in vite.config.ts's `share_target.action`, because a Vite config that imported
 * this module would pull idb-keyval into the build's own bundle.
 */
export const SHARE_TARGET_PATH = '/share';

/** The form field the pictures arrive in — `share_target.params.files[0].name`. */
export const SHARE_FILES_FIELD = 'photos';

/**
 * Names the hand-off, in the URL the worker redirects to and in the litter's
 * own. `shared` rather than `id`, because on `/litters/{id}?photos=add&…` an
 * `id` reads as the litter's.
 */
export const SHARE_ID_PARAM = 'shared';

const KEY = 'puppy-doctor:shared-photos';

type Handover = { id: string; files: File[] };

const isHandover = (value: unknown): value is Handover => {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.id === 'string' &&
    Array.isArray(record.files) &&
    record.files.every((file) => file instanceof File)
  );
};

/**
 * The worker's half: park what the share sheet sent and name it.
 *
 * Rejects rather than swallowing — a quota that has no room for the pictures is
 * something the redirect has to know about, so that the landing page can say so
 * instead of opening an empty wizard.
 */
export const holdSharedPhotos = async (files: readonly File[]): Promise<string> => {
  const id = globalThis.crypto.randomUUID();
  await set(KEY, { id, files: [...files] } satisfies Handover);
  return id;
};

/**
 * How many pictures are waiting under this id, without taking them.
 *
 * The landing screen asks, because with more than one litter in the kennel it
 * has a question to put first — and "3 photos: which litter?" is a different
 * screen from "nothing arrived". Deliberately does NOT consume: the screen that
 * asks is not the screen that uses them.
 */
export const countSharedPhotos = async (id: string): Promise<number> => {
  try {
    const stored: unknown = await get(KEY);
    return isHandover(stored) && stored.id === id ? stored.files.length : 0;
  } catch {
    return 0;
  }
};

const take = async (id: string): Promise<readonly File[]> => {
  try {
    const stored: unknown = await get(KEY);
    await del(KEY);
    return isHandover(stored) && stored.id === id ? stored.files : [];
  } catch {
    return [];
  }
};

/**
 * In flight, and only while it is.
 *
 * Two things claim the same id in the same tick — React's development
 * double-mount is the one that always happens — and the second would find a
 * record the first had already deleted. Sharing the promise makes both callers
 * the same read. It is dropped as soon as that read finishes, and that is the
 * point rather than tidiness: the wizard is reachable again with `shared` still
 * in the URL (the back button, a reopened tab), and a claim that kept answering
 * would upload the same afternoon's pictures a second time.
 */
let inFlight: { id: string; files: Promise<readonly File[]> } | null = null;

/**
 * The page's half: the pictures, once. Every later call for the same id answers
 * with none, which is the honest reading of "these have already been taken".
 */
export const claimSharedPhotos = (id: string): Promise<readonly File[]> => {
  if (inFlight?.id === id) return inFlight.files;

  const files = take(id);
  inFlight = { id, files };
  void files.then(() => {
    if (inFlight?.id === id) inFlight = null;
  });

  return files;
};
